import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, ButtonGroup, Fade, ImageList, Menu, MenuItem, useTheme } from "@mui/material";
import MemoItem from "../components/Item";
import SkipNextIcon from '@mui/icons-material/South';
import SkipPreviousIcon from '@mui/icons-material/North';
import useMediaQuery from "@mui/material/useMediaQuery";
import { countries } from "../services/Countries";

const getNearestNumber = (number, array, height, prev) => {
    let low = 0;
    let high = array.length;
    const target = prev ? number : number - height;
    while (low < high) {
        const middle = Math.floor((low + high) / 2);
        if (array[middle] <= target && prev) {
            low = middle + 1;
        } else if (array[middle] < target && !prev) {
            low = middle + 1;
        } else {
            high = middle;
        }
    }

    if (!prev) return array[low];
    if (low < array.length) return array[low];
    return array[array.length - 1] >= number - height
        ? array[array.length - 1]
        : undefined;
}

// Grid rows have a fixed height and gap, so measuring the first two rows is
// enough to derive every row offset without walking the full rendered list.
let cachedRowOffsets = null;
let cachedRowKey = "";
let cachedInsetTop = 0;
let cachedInsetBottom = 0;
let cachedRowHeight = 0;
let cachedRowStep = 0;
let cachedVisibleHeight = 0;

const readInset = (name) => {
    const value = parseFloat(getComputedStyle(document.documentElement).getPropertyValue(name));
    return isNaN(value) ? 0 : value;
}

const getRowOffsets = (ref, cols) => {
    const el = ref.current;
    const children = el.children;
    const len = children.length;
    const key = len + ":" + cols + ":" + window.innerWidth + ":" + window.innerHeight;
    if (cachedRowKey === key && cachedRowOffsets) {
        return cachedRowOffsets;
    }
    cachedInsetTop = readInset("--sat");
    cachedInsetBottom = readInset("--sab");
    const menuTop = document.querySelector(".MenuBar")?.getBoundingClientRect().top;
    cachedVisibleHeight = (menuTop ?? window.innerHeight - 64 - cachedInsetBottom) - cachedInsetTop;
    const firstRowBounds = children[0].getBoundingClientRect();
    const firstRowTop = firstRowBounds.top + window.scrollY - cachedInsetTop;
    const rowGap = parseFloat(getComputedStyle(el).rowGap) || 0;
    const rowStep = len > cols
        ? children[cols].getBoundingClientRect().top - firstRowBounds.top
        : firstRowBounds.height + rowGap;
    const rowCount = Math.ceil(len / cols);
    const offsets = Array.from({ length: rowCount }, (_, index) => firstRowTop + index * rowStep);
    cachedRowHeight = firstRowBounds.height;
    cachedRowStep = rowStep;
    cachedRowOffsets = offsets;
    cachedRowKey = key;
    return offsets;
}

const scrollToNextPosition = (ref, cols, cachedWindowScrollY, scrollBack) => {
    if (!ref.current || ref.current.children.length === 0) return;
    const offsets = getRowOffsets(ref, cols);
    const currentScrollY = cachedWindowScrollY ?? window.scrollY;
    if (scrollBack) {
        const y = getNearestNumber(currentScrollY, offsets, cachedRowHeight);
        if (y) window.scrollTo({ top: y, behavior: "smooth" });
        return;
    }
    const currentRow = Math.max(0, Math.round((currentScrollY - offsets[0]) / cachedRowStep));
    const pageRows = Math.ceil(cachedVisibleHeight / cachedRowStep);
    const nextRow = currentScrollY <= cachedInsetTop ? pageRows - 1 : currentRow + pageRows;
    const y = offsets[Math.min(nextRow, offsets.length - 1)];
    if (y) window.scrollTo({ top: y, behavior: "smooth" });
}

const scrollToPreviousPosition = (ref, cols, cachedWindowScrollY) => {
    if (!ref.current || ref.current.children.length === 0) return;
    const offsets = getRowOffsets(ref, cols);
    const currentScrollY = cachedWindowScrollY ?? window.scrollY;
    const currentRow = Math.max(0, Math.round((currentScrollY - offsets[0]) / cachedRowStep));
    const pageRows = Math.ceil(cachedVisibleHeight / cachedRowStep);
    const y = offsets[Math.max(0, currentRow - pageRows)];
    if (y) window.scrollTo({ top: y, behavior: "smooth" });
}

export const getCols = (height, width) => {
    if (width < 275) return 1;
    if (width < 575) return 2;
    if (width < 775) return width > height ? 3 : 2;
    if (width < 975) return 3;
    if (width < 1175) return 4;
    if (width < 1375) return 5;
    return 6;
}
let startY = 0;
let startX = 0;
let distanceX = 0;
let distanceY = 0;
let cachedWindowScrollY = 0;
let time;
let viewedTime = new Date();
const ItemList = React.memo(ItemListBase);
function ItemListBase({
    enableQuickScroll,
    darkMode,
    leftHanded,
    showQuickScroll,
    alwaysShow,
    handlePreLoad,
    items,
    handleToggle,
    setLoading,
    sort,
    preload,
    openRef,
    imageSelections,
    recentItems,
    filterSellers,
    blockedCountries,
    blockedSellers,
    filterCountries,
    updateUser,
    isUser,
    sellersDisabled,
    countriesDisabled,
    refreshClick,
    firstItemRefresh,
    currentOverlayId,
    setCurrentOverlayId, autoRefresh, enableFirstItem
}) {
    const ref = useRef(null);
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
    const [cols, setCols] = useState(getCols(window.innerHeight, window.innerWidth));

    useEffect(() => {
        const detectSize = () => {
            setCols(getCols(window.innerHeight, window.innerWidth))
        }
        window.addEventListener('resize', detectSize)
        return () => {
            window.removeEventListener('resize', detectSize)
        }
    }, []);

    useEffect(() => {
        const visibilityChanged = () => {
            if (!autoRefresh) return;
            if (items.length === 0 || sort !== 'StartTimeNewest' || openRef.current) return;
            if (document.visibilityState === 'visible') {
                const difference = new Date() - viewedTime;
                if ((window.scrollY === 0 && difference > 150000) || (difference > 300000)) refreshClick();
            } else {
                viewedTime = new Date();
            }
        }
        document.addEventListener('visibilitychange', visibilityChanged);
        return () => {
            document.removeEventListener('visibilitychange', visibilityChanged);
        }
    }, [items, refreshClick, sort, openRef, autoRefresh]);

    useEffect(() => {
        if (items?.length === 0) {
            setCurrentOverlayId(0);
        }
    }, [items])

    const hasChildren = ref.current?.children?.length > 0;
    const disabledButtons = !hasChildren || items.length === 0;
    const hasItems = items.length !== 0;

    useEffect(() => {
        if (!ref.current) return;
        const reference = ref;
        const handleKeyDown = (event) => {
            if (!reference.current || openRef.current || !hasItems) return;
            if (event.key === 'ArrowUp') {
                event.preventDefault();
                scrollToPreviousPosition(reference, cols)
            } else if (event.key === 'ArrowDown') {
                event.preventDefault();
                scrollToNextPosition(reference, cols)
            }
        };

        const handleTouchStart = (event) => {
            if (!reference.current || openRef.current || !hasItems) return;
            startY = event.touches[0].clientY;
            startX = event.touches[0].clientX;
            distanceX = 0;
            distanceY = 0;
            cachedWindowScrollY = window.scrollY
            time = Date.now();
        };

        const handleTouchEnd = (event) => {
            if (!reference.current || openRef.current || !hasItems) return;
            event.preventDefault();
            event.stopPropagation();
            if (window.scrollY <= 0) return;
            const endY = event.changedTouches[0].clientY;
            const distanceRawY = Math.abs(endY - startY);
            const velocityRawY = distanceRawY / (Date.now() - time)
            if ((velocityRawY < 0.3 && distanceRawY < 30) || distanceX - distanceY > 0) {
                scrollToNextPosition(reference, cols, cachedWindowScrollY, true);
                return;
            }
            if (endY < startY) {
                scrollToNextPosition(reference, cols, cachedWindowScrollY);
            } else {
                scrollToPreviousPosition(reference, cols, cachedWindowScrollY);
            }
        };

        const touchmove = (e) => {
            if (!reference.current || openRef.current || !hasItems) return;
            const endX = e?.changedTouches?.[0]?.clientX;
            const endY = e?.changedTouches?.[0]?.clientY;
            const differenceX = startX - endX;
            const differenceY = startY - endY;
            distanceX = distanceX + Math.abs(differenceX);
            distanceY = distanceY + Math.abs(differenceY);
        }

        const onWheel = (e) => {
            if (!reference.current || openRef.current || !hasItems) return;
            if (e.deltaY > 0) {
                e.preventDefault();
                scrollToNextPosition(reference, cols);
            } else if (e.deltaY < 0) {
                e.preventDefault();
                scrollToPreviousPosition(reference, cols);
            }
        }

        window.addEventListener('keydown', handleKeyDown);
        if (enableQuickScroll) {
            reference.current.addEventListener('touchend', handleTouchEnd, { passive: false });
            reference.current.addEventListener('touchstart', handleTouchStart, { passive: false });
            reference.current.addEventListener('touchmove', touchmove, { passive: false });
            window.addEventListener('wheel', onWheel, { passive: false });
        } else {
            reference.current.removeEventListener('touchstart', handleTouchStart);
            reference.current.removeEventListener('touchend', handleTouchEnd)
            reference.current.removeEventListener('touchmove', touchmove);
            window.removeEventListener('wheel', onWheel);
        }
        return () => {
            if (!reference.current) return;
            window.removeEventListener('keydown', handleKeyDown);
            reference.current.removeEventListener('touchstart', handleTouchStart);
            reference.current.removeEventListener('touchend', handleTouchEnd);
            reference.current.removeEventListener('touchmove', touchmove);
            window.removeEventListener('wheel', onWheel);
        };
    }, [ref, openRef, cols, enableQuickScroll, hasItems])
    const searchParams = new URLSearchParams(window.location.search);
    const blockedSellersDisabled = searchParams.get('blockedSellers') === 'false';
    const blockedCountriesDisabled = searchParams.get('blockedCountries') === 'false';
    // Membership lookups run for every item on every list render. Backing them
    // with Sets keeps the per-item cost O(1) instead of O(blocked-list length).
    const filterSellersSet = useMemo(() => new Set(filterSellers), [filterSellers]);
    const blockedSellersSet = useMemo(() => new Set(blockedSellers), [blockedSellers]);
    const blockedCountriesSet = useMemo(() => new Set(blockedCountries), [blockedCountries]);
    const filterCountriesSet = useMemo(() => new Set(filterCountries), [filterCountries]);
    const recentItemsSet = useMemo(() => new Set(recentItems), [recentItems]);
    const [contextMenu, setContextMenu] = React.useState(null);

    const handleContextMenu = (event) => {
        event.stopPropagation();
        event.preventDefault();
        if (event.target.className?.includes?.("MuiImageList-root")) {
            setContextMenu(null);
            return;
        }
        setContextMenu(
            contextMenu === null
                ? {
                    mouseX: event.clientX + 2,
                    mouseY: event.clientY - 6,
                }
                :
                null,
        );
    };

    const handleClose = () => {
        setContextMenu(null);
    };

    const currentOverlayItem = useMemo(
        () => items.find((item) => item.itemId === currentOverlayId),
        [items, currentOverlayId]
    );

    useEffect(() => {
        const handleScroll = () => {
            setContextMenu(null);
        }
        window.addEventListener("scroll", handleScroll)
        return (() => {
            window.removeEventListener("scroll", handleScroll);
        })
    }, [])

    return (
        <>
            <Menu
                onContextMenu={handleContextMenu}
                open={contextMenu !== null}
                onClose={handleClose}
                anchorReference="anchorPosition"
                autoFocus={false}
                TransitionProps={{ unmountOnExit: true }}
                anchorPosition={
                    contextMenu !== null
                        ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
                        : undefined
                }
            >
                <MenuItem
                    disabled={!isUser || sellersDisabled || blockedSellersSet.has(currentOverlayItem?.seller?.username)}
                    onClick={() => {
                        const item = currentOverlayItem;
                        if (item) {
                            const seller = item?.seller?.username;
                            updateUser({ blockedSellers: seller }, null, `'${seller}' added to blocked sellers`, null, true, "Are you sure you want to block '" + seller + "'?", "Block", "Block seller", seller);
                        }
                        handleClose();
                    }}>Block seller</MenuItem>
                <MenuItem
                    disabled={!isUser || countriesDisabled || blockedCountriesSet.has(currentOverlayItem?.itemLocation?.country)}
                    onClick={() => {
                        const item = currentOverlayItem;
                        if (item) {
                            const country = item.itemLocation?.country;
                            updateUser({ blockedCountries: country }, null, `'${countries[country]}' added to blocked countries`, null, true, "Are you sure you want to block '" + countries[country] + "'?", "Block", "Block country", country, true);
                        }
                        handleClose();
                    }}>Block country</MenuItem>
            </Menu>
            <Fade in={items.length !== 0}>
                <ImageList className="ListingGrid" onContextMenu={handleContextMenu} ref={ref} onTouchStart={() => {
                }}
                    sx={{ overflow: "hidden", paddingBottom: items.length !== 0 ? '4em' : undefined }}
                    cols={cols} gap={2}>
                    {items.map((item, i) => {
                        const isFirstItemRefresh = enableFirstItem && i !== 0 && firstItemRefresh?.itemId === item.itemId;
                        let firstItemPositionTop;
                        if (isFirstItemRefresh) firstItemPositionTop = ((i + 1) % cols) === 1;
                        const filterSeller = !blockedSellersDisabled &&
                            (filterSellersSet.has(item.seller?.username) || blockedSellersSet.has(item.seller?.username))
                        const filterCountry = !blockedCountriesDisabled &&
                            (blockedCountriesSet.has(item.itemLocation?.country) || filterCountriesSet.has(item.itemLocation?.country));
                        const isFirst = i === 0;
                        return <MemoItem firstItemPositionTop={firstItemPositionTop}
                            isFirstItemRefresh={isFirstItemRefresh}
                            isFilterSeller={filterSeller || filterCountry}
                            isClicked={recentItemsSet.has(item.itemId)}
                            alwaysShow={alwaysShow}
                            preload={preload?.id === item.legacyItemId ? preload : null}
                            handlePreLoad={handlePreLoad} key={item.itemId}
                            selectedImageUrl={imageSelections[item.itemId]?.imageUrl}
                            item={item} handleToggle={handleToggle} setLoading={setLoading}
                            isFirst={i === 0}
                            isCurrentOverlayId={currentOverlayId === item.itemId}
                            setCurrentOverlayId={setCurrentOverlayId} sort={sort} />
                    })
                    }
                </ImageList>
            </Fade>
            {showQuickScroll &&
                <Fade in={!isDesktop}>
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent={leftHanded ? "left" : "right"}
                        width="100%"
                        height={"4em"}
                        sx={{
                            pointerEvents: "none",
                            zIndex: 0,
                            position: "fixed",
                            top: `calc(30vh - 31px)`,
                            ...(leftHanded ? { left: -1 } : { right: -1 })
                        }}
                    >
                        <ButtonGroup className={leftHanded ? "left" : undefined} sx={{
                            backdropFilter: "blur(20px)",
                            pointerEvents: "all",
                            backgroundColor: darkMode ? "rgba(0,0,0,0.7)" : "rgba(255,255,255,0.7)"
                        }} orientation={"vertical"}>
                            <Button sx={{
                                border: "none",
                                borderRadius: 0, ...(leftHanded ? { borderTopRightRadius: "12px" } : { borderTopLeftRadius: "12px" }),
                                height: "64px"
                            }} disabled={disabledButtons} onClick={() => {
                                scrollToPreviousPosition(ref, cols)
                            }}><SkipPreviousIcon /></Button>
                            <Button sx={{
                                border: "none",
                                borderRadius: 0, ...(leftHanded ? { borderBottomRightRadius: "12px" } : { borderBottomLeftRadius: "12px" }),
                                height: "64px"
                            }} disabled={disabledButtons} onClick={() => {
                                scrollToNextPosition(ref, cols)
                            }} color={"primary"}><SkipNextIcon /></Button>
                        </ButtonGroup>
                    </Box>
                </Fade>
            }
        </>
    )
}

export default ItemList;